import { useState, useCallback, useRef } from 'preact/hooks';
import { fetchOnce, prepareBody } from '@shared/hooks/http';
import { apiBase } from '../api/api-base';
import { toast } from 'react-toastify';
import { objectToFormData } from '../helpers/object-to-form-data';

// XHR is the only browser transport that reports upload progress (`fetch` has no
// `upload.onprogress` equivalent). Used only when a caller opts in via `trackProgress`,
// so every other usePost caller keeps using fetchOnce (with its 401 session-recovery
// retry) unchanged. A 401 here just surfaces as a normal error — this path intentionally
// doesn't reimplement Venus's auth-retry registry.
function postFormDataWithProgress(url, formData, { signal, onProgress, headers = {} } = {}) {
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', url, true);
        xhr.withCredentials = true;

        // Do not set Content-Type here — the browser must generate it (with the
        // multipart boundary) from the FormData itself, same as the fetchOnce path
        // leaves it unset for FormData bodies (see prepareBody's isRawBody check).
        for (const [key, value] of Object.entries(headers)) {
            if (key.toLowerCase() !== 'content-type') {
                xhr.setRequestHeader(key, value);
            }
        }

        if (signal) {
            if (signal.aborted) {
                reject(new DOMException('Aborted', 'AbortError'));
                return;
            }
            signal.addEventListener('abort', () => xhr.abort(), { once: true });
        }

        xhr.upload.onprogress = (event) => {
            if (event.lengthComputable) {
                onProgress?.(Math.round((event.loaded / event.total) * 100));
            }
        };

        xhr.onabort = () => reject(new DOMException('Aborted', 'AbortError'));
        xhr.onerror = () => reject(new Error('Network error during upload'));

        xhr.onload = () => {
            const parseBody = () => {
                if (!xhr.responseText) return null;
                try {
                    return JSON.parse(xhr.responseText);
                } catch {
                    return xhr.responseText;
                }
            };

            if (xhr.status >= 200 && xhr.status < 300) {
                resolve(parseBody());
            } else {
                const err = new Error(`Request failed with status ${xhr.status}`);
                err.status = xhr.status;
                err.body = parseBody();
                reject(err);
            }
        };

        xhr.send(formData);
    });
}

export function usePost(
    endpoint,
    {
        asFormData = false,
        autoCancelPrevious = true,
        successMessage = 'Successfully captured data!',
        trackProgress = false,
    } = {}
) {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [data, setData] = useState(null);
    const [progress, setProgress] = useState(0);

    const controllerRef = useRef(null);

    const cancel = useCallback(() => {
        try {
            controllerRef.current?.abort();
        } finally {
            controllerRef.current = null;
        }
    }, []);

    const post = useCallback(
        async (body, config = {}) => {
            if (autoCancelPrevious && controllerRef.current) {
                controllerRef.current.abort();
                controllerRef.current = null;
            }

            const controller = new AbortController();
            controllerRef.current = controller;

            setIsSubmitting(true);
            setError(null);
            setData(null);
            setProgress(0);
            try {
                const rawBody = asFormData ? objectToFormData(body ?? {}) : body;

                let result;
                if (asFormData && trackProgress) {
                    result = await postFormDataWithProgress(`${apiBase}${endpoint}`, rawBody, {
                        signal: config.signal ?? controller.signal,
                        onProgress: setProgress,
                        headers: config.headers ?? {},
                    });
                } else {
                    const { body: prepared, headers } = prepareBody(rawBody, config.headers ?? {});

                    result = await fetchOnce(`${apiBase}${endpoint}`, {
                        method: 'POST',
                        credentials: 'include',
                        headers,
                        body: prepared,
                        signal: config.signal ?? controller.signal,
                    });
                }

                const resolved = result ?? { success: true };
                setData(resolved);
                toast.success(successMessage);
                return resolved;
            } catch (err) {
                if (err?.name === 'AbortError') return Promise.reject(err);
                toast.error(err?.body?.detail ?? err?.message ?? 'Something went wrong!');
                setError(err);
                throw err;
            } finally {
                setIsSubmitting(false);
            }
        },
        [endpoint, asFormData, autoCancelPrevious, trackProgress]
    );

    return { post, cancel, isSubmitting, error, data, progress };
}
